// EventVenueUploadPage.jsx
import React, { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react"; // ← 로딩 아이콘

/**
 * 행사장 목록 엑셀 업로드 페이지
 *
 * [데이터 형식 안내]
 *  - 엑셀 첫 번째 시트만 사용합니다.
 *  - 3번째 행부터 실제 데이터가 존재해야 합니다. (1,2행은 헤더/설명)
 *  - 열 순서와 개수는 아래 예시와 동일해야 합니다:
 *
 *    (ID, 사업장명, 사업장명(변환), 상세 주소, 지역구분, 시군구, 세부공간명, 최소(명), 최대(명))
 */
export default function EventVenueUploadPage() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState("");

    // 로딩 상태 추가
    const [loading, setLoading] = useState(false);

    // 파일 선택 시 상태에 저장
    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setUploadStatus(""); // 새 파일 선택 시 상태 초기화
    };

    // 파일 업로드 실행
    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadStatus("업로드할 파일을 선택해주세요.");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        // 업로드 시작
        setLoading(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/event-venue/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            setUploadStatus(response.data || "업로드가 완료되었습니다.");
        } catch (error) {
            console.error(error);
            setUploadStatus("업로드에 실패했습니다. 파일 형식을 확인해주세요.");
        } finally {
            // 업로드 끝
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen">
            <main className="max-w-7xl mx-auto p-6 space-y-6">
                <div className="bg-white rounded-xl shadow p-4">
                    <h2 className="text-xl font-bold mb-4">행사장 목록 엑셀 업로드</h2>

                    <p className="text-sm text-gray-600 mb-4">
                        <b className="text-red-400">
                            업로드 시 기존의 모든 행사장 데이터가 초기화됩니다.
                        </b>
                        <br />
                        <strong>엑셀 업로드 유의사항:</strong>
                        <br />
                        1) 엑셀 <strong>첫 번째 시트</strong>만 사용
                        <br />
                        2) <strong>3번째 행부터</strong> 실제 데이터가 존재해야 함 (1,2행은 헤더/설명)
                        <br />
                        3) 열 순서와 개수는 예시 표와 <strong>동일</strong>해야 합니다.
                    </p>

                    {/* 예시 데이터 표 */}
                    <h3 className="text-lg font-semibold mt-6 mb-2">[예시 데이터 표]</h3>
                    <div className="overflow-x-auto mb-6">
                        <table className="w-full table-auto border-collapse text-sm text-left">
                            <thead className="bg-gray-100 border-b border-gray-300">
                            <tr>
                                <th className="px-3 py-2 border-r border-gray-300">ID</th>
                                <th className="px-3 py-2 border-r border-gray-300">사업장명</th>
                                <th className="px-3 py-2 border-r border-gray-300">
                                    사업장명(변환)
                                </th>
                                <th className="px-3 py-2 border-r border-gray-300">
                                    상세 주소
                                </th>
                                <th className="px-3 py-2 border-r border-gray-300">
                                    지역구분
                                </th>
                                <th className="px-3 py-2 border-r border-gray-300">시군구</th>
                                <th className="px-3 py-2 border-r border-gray-300">
                                    세부공간명
                                </th>
                                <th className="px-3 py-2 border-r border-gray-300">
                                    최소(명)
                                </th>
                                <th className="px-3 py-2">최대(명)</th>
                                <th className="px-3 py-2">비고</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            <tr>
                                <td className="px-3 py-2">6</td>
                                <td className="px-3 py-2">스탠포드 리조트</td>
                                <td className="px-3 py-2">
                                    스탠포드 호텔 앤 리조트 통영
                                </td>
                                <td className="px-3 py-2">
                                    경상남도 통영시 도남로 347(도남동)
                                </td>
                                <td className="px-3 py-2">16경남</td>
                                <td className="px-3 py-2">통영시</td>
                                <td className="px-3 py-2">세미나룸 1,2</td>
                                <td className="px-3 py-2">40</td>
                                <td className="px-3 py-2">60</td>
                                <td className="px-3 py-2">세미나 룸 1,2 통합가능</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2">7</td>
                                <td className="px-3 py-2">스탠포드 리조트</td>
                                <td className="px-3 py-2">
                                    스탠포드 호텔 앤 리조트 통영
                                </td>
                                <td className="px-3 py-2">
                                    경상남도 통영시 도남로 347(도남동)
                                </td>
                                <td className="px-3 py-2">16경남</td>
                                <td className="px-3 py-2">통영시</td>
                                <td className="px-3 py-2">아틀라스</td>
                                <td className="px-3 py-2">60</td>
                                <td className="px-3 py-2">100</td>
                                <td className="px-3 py-2"></td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2">8</td>
                                <td className="px-3 py-2">풍기관광호텔</td>
                                <td className="px-3 py-2">풍기 관광호텔 영주</td>
                                <td className="px-3 py-2">
                                    경상북도 영주시 풍기읍 풍기로 53
                                </td>
                                <td className="px-3 py-2">15경북</td>
                                <td className="px-3 py-2">영주시</td>
                                <td className="px-3 py-2">스카이뷰</td>
                                <td className="px-3 py-2">200</td>
                                <td className="px-3 py-2">200</td>
                                <td className="px-3 py-2"></td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2">9</td>
                                <td className="px-3 py-2">더리버사이드호텔</td>
                                <td className="px-3 py-2">더 리버사이드 호텔 서울</td>
                                <td className="px-3 py-2">
                                    서울시 서초구 강남대로107길 6(잠원동)
                                </td>
                                <td className="px-3 py-2">01서울</td>
                                <td className="px-3 py-2">-</td>
                                <td className="px-3 py-2">콘서트홀</td>
                                <td className="px-3 py-2">220</td>
                                <td className="px-3 py-2">810</td>
                                <td className="px-3 py-2"></td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* 파일 선택 */}
                    <div className="flex items-center gap-4 mb-4">
                        <input
                            type="file"
                            accept=".xlsx"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100
                                focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* 업로드 버튼 */}
                    <button
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                        onClick={handleUpload}
                    >
                        업로드
                    </button>

                    {/* 업로드 결과 메시지 */}
                    {uploadStatus && (
                        <div className="mt-2 text-sm text-green-600">{uploadStatus}</div>
                    )}
                </div>
            </main>

            {/* 로딩 오버레이 (loading이 true일 때만) */}
            {loading && (
                <div className="fixed inset-0 bg-gray-100 bg-opacity-40 flex items-start justify-center pt-[30vh] z-50">
                    <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
                        <Loader2 className="animate-spin w-5 h-5 text-blue-600" />
                        업로드 중입니다...
                    </div>
                </div>
            )}
        </div>
    );
}